import { getDefaultMetricDisplay } from "helpers/metrics"
import type { DrilldownSeed } from "connected-components/dashboard/drilldown/types"
import type { TemplateChartSettings } from "connected-components/dashboard/store/template-chart-settings"

// Per-template chart-settings config: the templateKey, the chart's default toggles, and an optional
// drilldown initial-value resolver.
//
// Drilldown: clicking a factor in another chart opens (pins) an asset-view panel focused on that
// factor, carrying a `DrilldownSeed` (the view / category / factor that was clicked). `resolveSeed`
// maps that payload to THIS template's initial settings, so the panel opens already showing the
// drilled-into category instead of the template's generic default. It returns null for an ordinary
// (non-drilldown) panel, which then just uses `defaults`. The reader hook applies the result once
// (existence-guarded) — it sets only the initial value; the user can change it afterwards.
//
// Resolvers are module-level so their reference stays stable for the reader hook's memo.

export type DrilldownSeedResolver = (drilldownSeed: DrilldownSeed | null) => TemplateChartSettings | null

export interface AssetViewChartConfig {
  templateKey: string
  defaults: TemplateChartSettings
  resolveSeed?: DrilldownSeedResolver
}

// risk / performance / content-set: open on the drilled-into category when the seed carries one.
const seedCategoryIfPresent: DrilldownSeedResolver = (seed) =>
  seed?.selectedCategory ? { selectedCategory: seed.selectedCategory } : null

// exposure / factor-sensitivity: these views show only individual factor categories, so the
// aggregated "factors" sentinel is meaningless here and dropped. A drilldown also forces
// selectedMetricDisplay: null, which the chart reads as "show the dual contribution + standalone columns".
const seedFactorLevelCategory: DrilldownSeedResolver = (seed) => {
  if (!seed) return null
  const category = seed.selectedCategory && seed.selectedCategory !== "factors" ? seed.selectedCategory : undefined
  return { selectedMetricDisplay: null, ...(category && { selectedCategory: category }) }
}

export const ASSET_VIEW_RISK_CHART: AssetViewChartConfig = {
  templateKey: "risk",
  defaults: { selectedCategory: "factors" },
  resolveSeed: seedCategoryIfPresent,
}

export const ASSET_VIEW_PERFORMANCE_CHART: AssetViewChartConfig = {
  templateKey: "performance",
  defaults: { selectedMetricDisplay: getDefaultMetricDisplay("performance"), selectedCategory: "factors" },
  resolveSeed: seedCategoryIfPresent,
}

export const ASSET_VIEW_EXPOSURE_CHART: AssetViewChartConfig = {
  templateKey: "exposure",
  defaults: { selectedCategory: "style", selectedMetricDisplay: getDefaultMetricDisplay("exposure") },
  resolveSeed: seedFactorLevelCategory,
}

export const ASSET_VIEW_BETA_CHART: AssetViewChartConfig = {
  templateKey: "beta",
  defaults: { selectedMetricDisplay: getDefaultMetricDisplay("beta") },
}

export const ASSET_VIEW_CONTENT_SET_CHART: AssetViewChartConfig = {
  templateKey: "content-set",
  defaults: { selectedCategory: "overall", selectedMetricDisplay: getDefaultMetricDisplay("contentSet") },
  resolveSeed: seedCategoryIfPresent,
}

// Predicted and historical share one key (the pre-RTK subtype was single, unlike graph's split).
export const ASSET_VIEW_FACTOR_SENSITIVITY_CHART: AssetViewChartConfig = {
  templateKey: "factor-sensitivity",
  defaults: { selectedMetricDisplay: getDefaultMetricDisplay("factorSensitivity"), selectedCategory: "style" },
  resolveSeed: seedFactorLevelCategory,
}
